/**
 * Shrink a photo in the browser before uploading: longest side at most `maxSide`
 * px, re-encoded as JPEG at `quality`. A 3–8 MB phone photo comes out around
 * 150–400 KB — plenty for print and PDF. Falls back to the original file when
 * the browser can't decode it, or when the result wouldn't be smaller.
 */
export async function compressImage(file: File | Blob, maxSide = 1600, quality = 0.8): Promise<File | Blob> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.type === 'image/svg+xml') return file;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) { bitmap.close(); return file; }
  ctx.fillStyle = '#fff'; // transparent PNGs get a white background instead of black
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', quality));
  if (!blob || blob.size >= file.size) return file;
  const base = file instanceof File && file.name ? file.name.replace(/\.[^.]+$/, '') : 'photo';
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
}
